// components/shared/banque/ChangeStatusModal.tsx
import React, { useState } from "react";
import { X, RefreshCw, CheckCircle, Shield, AlertCircle } from "lucide-react";
import { BankTransfertItem } from "../../../../types/features/administration/VirementBancaire";
import BaseModal from "../../../ui/Modal";


interface ChangeStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  transfertItem: BankTransfertItem;
  onConfirm: (item: BankTransfertItem, newStatus: string, newPayoutStatus: string) => Promise<void>;
}

export default function ChangeStatusModal({ isOpen, onClose, transfertItem, onConfirm }: ChangeStatusModalProps) {
  const [selectedPayoutStatus, setSelectedPayoutStatus] = useState("COMPLETED");
  const [selectedTransactionStatus, setSelectedTransactionStatus] = useState("COMPLETED");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const { transfert, details } = transfertItem;

  const formatAmount = (amount: number, currency: string) => {
    return `${amount.toLocaleString()} ${currency === "CAD" ? "CAD" : "FCFA"}`;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onConfirm(transfertItem, selectedTransactionStatus, selectedPayoutStatus);
      onClose();
    } catch (error) {
      console.error("Erreur lors du changement de statut:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title="Changer le statut" >
      <div className="w-full  bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-green-600 to-green-700 rounded-t-2xl">
          <h2 className="flex items-center gap-2 text-xl font-bold text-white">
            <Shield className="w-5 h-5" />
            Changer le statut
          </h2>
          <button onClick={onClose} className="p-1 text-white rounded-lg hover:bg-green-800">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Infos transfert */}
          <div className="p-3 bg-gray-100 rounded-lg">
            <p className="text-xs text-gray-500">Référence</p>
            <p className="font-mono font-bold text-sm">{transfert.txt_reference}</p>
          </div>

          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600">Montants</p>
            <p className="font-bold">
              {formatAmount(transfert.sending_amount, "CAD")} → {formatAmount(transfert.receiving_amount, "XAF")}
            </p>
            <p className="text-xs text-gray-500 mt-1">Frais: {formatAmount(transfert.fee, "CAD")}</p>
          </div>

          <div className="p-3 bg-gray-100 rounded-lg">
            <p className="text-xs text-gray-500">Bénéficiaire</p>
            <p className="text-sm font-semibold">{details.bank_account_owner}</p>
            <p className="text-xs text-gray-500">{details.bank_name}</p>
          </div>

          {/* Selecteurs */}
          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">Statut du Payout</label>
            <select
              value={selectedPayoutStatus}
              onChange={(e) => setSelectedPayoutStatus(e.target.value)}
              className="w-full p-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="COMPLETED">COMPLETED - Paiement complété</option>
              <option value="IN_PROGRESS">IN_PROGRESS - En cours</option>
              <option value="PENDING">PENDING - En attente</option>
              <option value="PENDING_MANUAL_PAYOUT">PENDING_MANUAL_PAYOUT - Paiement manuel</option>
              <option value="FAILED">FAILED - Échoué</option>
              <option value="CANCELLED">CANCELLED - Annulé</option>
            </select>
          </div>

          <div>
            <label className="block mb-2 text-sm font-bold text-gray-700">Statut du Transfert</label>
            <select
              value={selectedTransactionStatus}
              onChange={(e) => setSelectedTransactionStatus(e.target.value)}
              className="w-full p-3 border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="COMPLETED">COMPLETED - Complété</option>
              <option value="SUCCEEDED">SUCCEEDED - Réussi</option>
              <option value="PROCESSING">PROCESSING - En traitement</option>
              <option value="IN_PROGRESS">IN_PROGRESS - En cours</option>
              <option value="PENDING">PENDING - En attente</option>
              <option value="FAILED">FAILED - Échoué</option>
              <option value="CANCELLED">CANCELLED - Annulé</option>
            </select>
          </div>

          {/* Avertissement */}
          <div className="flex gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
            <p className="text-xs text-yellow-800">
              <span className="font-bold">Attention :</span> Cette action va modifier les statuts du transfert.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 bg-gray-50 rounded-b-2xl border-t">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 font-semibold text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex items-center justify-center flex-1 gap-2 px-4 py-2 font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-400"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Confirmer
          </button>
        </div>
      </div>
    </BaseModal>
  );
}
